import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createRestaurant,
  updateRestaurant,
  uploadRestaurantLogo,
  deleteRestaurantLogo,
} from '../../services/restaurant/restaurantService';
import { updateSubscriptionQuantity } from '../../services/subscription/subscriptionService';
import { useAuth } from '../../services/authentication/authContext';
import { Info } from 'lucide-react';
import { hasFreeSubscription } from '../../services/accessManagement/accessManagement';
import FileDropzone from '../ui/FileDropzone';
import RestaurantLogo from '../ui/RestaurantLogo';

// Mirrors S3BucketService.saveImage's 5 MB cap.
const MAX_LOGO_BYTES = 5 * 1024 * 1024;

interface SaveRestaurantModalProps {
  isOpen: boolean;
  onClose: () => void;
  // mode: 'create' or 'edit'
  mode: 'create' | 'edit';
  // If editing, pass the existing restaurant’s id and name
  initialId?: string;
  initialName?: string;
  // Edit mode only: current logo URL (null = none). Powers the preview.
  initialLogoUrl?: string | null;
  // Called on close when the logo was uploaded/removed — those persist on their
  // own endpoints, so the parent still has to refresh its restaurant data.
  onLogoChanged?: () => void;
  onSave: (name: string, id?: string) => Promise<void>;
}

export default function SaveRestaurantModal({
  isOpen,
  onClose,
  mode,
  initialId,
  initialName,
  initialLogoUrl,
  onLogoChanged,
  onSave,
}: SaveRestaurantModalProps) {
  const { t } = useTranslation();
  const { backendUser, accountSettings } = useAuth();
  const [name, setName] = useState(initialName || '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Logo state. Upload/remove hit their own endpoints immediately (separate from
  // the name PUT), so `logoDirty` tracks whether the store still needs a refresh
  // when the modal closes without pressing "Save changes".
  const [logoUrl, setLogoUrl] = useState<string | null>(initialLogoUrl ?? null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoDirty, setLogoDirty] = useState(false);

  // Check if account has free subscription setting
  const isFreeSubscriptionUser = hasFreeSubscription(backendUser, accountSettings);

  // When the modal opens (or mode/initialName changes), reset fields
  useEffect(() => {
    if (isOpen) {
      setName(initialName || '');
      setError(null);
      setSaving(false);
      setLogoUrl(initialLogoUrl ?? null);
      setLogoBusy(false);
      setLogoError(null);
      setLogoDirty(false);
    }
  }, [isOpen, initialName, initialLogoUrl]);

  // Logo changes are already persisted server-side; closing still has to tell the
  // parent to refresh so the new logo shows everywhere. Separate from onSave —
  // the name save path stays untouched.
  const handleClose = () => {
    if (logoDirty) {
      setLogoDirty(false);
      onLogoChanged?.();
    }
    onClose();
  };

  const handleLogoSelected = async (files: File[]) => {
    const file = files[0];
    if (!file || !initialId) return;

    // Cheap client-side guard so a huge drop isn't buffered and uploaded just to
    // be rejected. The server's 5 MB cap stays the authority.
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError(t('restaurantModal.logo.tooLarge'));
      return;
    }

    setLogoBusy(true);
    setLogoError(null);
    try {
      const updated = await uploadRestaurantLogo(initialId, file);
      setLogoUrl(updated.logoUrl);
      setLogoDirty(true);
    } catch (err: any) {
      console.error(err);
      // The two guard rejections worth naming (server messages are English-only,
      // so map them onto translated copy instead of showing them raw).
      const message: string = err?.message ?? '';
      setLogoError(
        message.includes('File size too large')
          ? t('restaurantModal.logo.tooLarge')
          : message.includes('Invalid file type') || message.includes('must be a PNG')
          ? t('restaurantModal.logo.invalidType')
          : t('restaurantModal.logo.uploadFailed')
      );
    } finally {
      setLogoBusy(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!initialId) return;

    setLogoBusy(true);
    setLogoError(null);
    try {
      const updated = await deleteRestaurantLogo(initialId);
      setLogoUrl(updated.logoUrl);
      setLogoDirty(true);
    } catch (err) {
      console.error(err);
      setLogoError(t('restaurantModal.logo.removeFailed'));
    } finally {
      setLogoBusy(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError(t('restaurantModal.nameRequired'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (mode === 'create') {
        // Only update subscription quantity if user is not in free subscription list
        if (!isFreeSubscriptionUser) {
          try {
            await updateSubscriptionQuantity(1);
          } catch (subscriptionErr: any) {
            console.error('Failed to update subscription:', subscriptionErr);
            setError(
              subscriptionErr.message || 'Failed to update subscription. Please try again.'
            );
            setSaving(false);
            return; // Don't proceed with restaurant creation if subscription update fails
          }
        }

        // If subscription update succeeded (or was skipped for free users), create the restaurant
        await createRestaurant(name.trim());
      } else {
        await updateRestaurant(initialId!, name.trim());
      }
      await onSave(name.trim(), mode === 'edit' ? initialId : undefined);
      onClose();
    } catch (err: any) {
      console.error(err);
      setError(
        mode === 'create'
          ? t('restaurantModal.couldNotCreate')
          : t('restaurantModal.couldNotUpdate')
      );
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50" onClick={handleClose}>
      <div
        className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-md border border-zinc-200 dark:border-white/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-50">
              {mode === 'create' ? t('restaurantModal.addNewRestaurant') : t('restaurantModal.editRestaurant')}
            </h2>
            <button
              onClick={handleClose}
              className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              aria-label={t('restaurantModal.close')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
              </svg>
            </button>
          </div>

          {/* Form */}
          <div className="space-y-5">
            <div>
              <label htmlFor="restaurantName" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                {t('restaurantModal.restaurantName')} <span className="text-red-500 dark:text-red-400">{t('restaurantModal.required')}</span>
              </label>
              <input
                type="text"
                id="restaurantName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:focus:ring-emerald-500/20 focus:border-emerald-500 transition"
                placeholder={t('restaurantModal.enterRestaurantName')}
              />
            </div>

            {/* Logo — edit mode only: create has no restaurant id to upload against yet */}
            {mode === 'edit' && initialId && (
              <div>
                <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                  {t('restaurantModal.logo.label')}
                </label>

                <div className="flex items-center gap-4 mb-3">
                  <RestaurantLogo logoUrl={logoUrl} name={name || initialName || ''} size="large" />
                  <div className="min-w-0">
                    <p className="text-sm text-zinc-700 dark:text-zinc-300">
                      {logoUrl ? t('restaurantModal.logo.replace') : t('restaurantModal.logo.upload')}
                    </p>
                    {logoUrl && (
                      <button
                        type="button"
                        onClick={handleLogoRemove}
                        disabled={logoBusy}
                        className="mt-1 text-sm font-medium text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                      >
                        {t('restaurantModal.logo.remove')}
                      </button>
                    )}
                  </div>
                </div>

                <FileDropzone
                  files={[]}
                  onFilesChange={handleLogoSelected}
                  accept="image/png,image/jpeg,image/webp"
                  disabled={logoBusy}
                  hint={t('restaurantModal.logo.hint')}
                />

                {logoBusy && (
                  <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
                    {t('restaurantModal.logo.busy')}
                  </p>
                )}

                {logoError && (
                  <div className="mt-2 p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 text-sm text-red-600 dark:text-red-400">
                    {logoError}
                  </div>
                )}
              </div>
            )}

            {/* Subscription info for create mode (only for non-free users) */}
            {mode === 'create' && !isFreeSubscriptionUser && (
              <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/50">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                      Subscription Update
                    </h3>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Adding a new restaurant will automatically increase your subscription quantity by 1.
                      You'll be charged immediately for the additional restaurant (prorated for the current billing period).
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition disabled:opacity-50"
              >
                {t('restaurantModal.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !name.trim()}
                className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving
                  ? mode === 'create'
                    ? t('restaurantModal.creating')
                    : t('restaurantModal.saving')
                  : mode === 'create'
                  ? t('restaurantModal.create')
                  : t('restaurantModal.saveChanges')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
